import React, { useContext, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Pressable } from 'react-native'
import { connect, useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { scroller } from 'react-scroll'

import { bindActionCreators } from 'redux'
import { useTheme } from 'styled-components/native'
import { v4 as uuidv4 } from 'uuid'

import { UNAUTHENTICATED_REDIRECT_URL } from '@lyrahealth-inc/lyraweb-mobile/src/hooks/useShareSessionStorageAcrossTabs'
import {
  AUTO_ENROLLMENT_PATHWAYS_EVENTS,
  COMMON_MIXPANEL_PAGE,
  COMMON_MIXPANEL_PROPERTIES,
  COVERAGE_MESSAGING_EVENTS,
  CustomerPropertiesMap,
  DirectLinkSource,
  IS_PRODUCTION,
  MicrositeConfig,
  PAGE_ROUTES,
  PATHWAYS_ENTRY_POINTS,
  PATHWAYS_EVENTS,
  PATHWAYS_PAGE,
  PATHWAYS_PROPERTIES,
  PATHWAYS_TYPE,
  PRODUCTION_REFER_ORGANIZATION_LINK,
  PROGRAM_MEMBERSHIP_TYPES,
  REGISTRATION_EVENTS,
  SessionLimitByProgram,
  Theme,
  useFlags,
  useGetPathwaysVersion,
  useLyraIntl,
  User,
} from '@lyrahealth-inc/shared-app-logic'
import { useLDClient } from '@lyrahealth-inc/shared-app-logic/src/features/flags/launchdarkly-client-sdk.web'
import { ContentLayout } from '@lyrahealth-inc/ui-core'
import {
  AppPromoBanner,
  AppPromoBottomSheet,
  CoverageMessagingButton,
  FindEmployer,
  FindEmployerFormValues,
  LyraLogo,
  ProductTypeContext,
  StickyView,
  TextButton,
  ThemeProvider,
  tID,
  ToastContent,
  toJS,
} from '@lyrahealth-inc/ui-core-crossplatform'
import {
  LyraSelectMicrositeHomePage,
  PathwaysMicrositeHomePage,
} from '@lyrahealth-inc/ui-core-crossplatform/src/microsite'

import ChatLive from '../../../../common/components/chatLive/ChatLive'
import LegalFooter from '../../../../common/components/footer/LegalFooter'
import DefaultHeader from '../../../../common/components/header/DefaultHeader'
import Header from '../../../../common/components/header/Header'
import { CYPRESS_CARE_REFERENCE_ID, MOBILE_PROMO_DISMISSED } from '../../../../common/constants/appConstants'
import { useDefaultMixpanelEntryPoint } from '../../../../common/hooks/useDefaultMixpanelEntryPoint'
import { useGetCostMessagingInfo } from '../../../../common/hooks/useGetCostMessagingInfo'
import { useIsCostMessagingEnabled } from '../../../../common/hooks/useIsCostMessagingEnabled'
import { useIsMultiCtaPathwaysAvailable } from '../../../../common/hooks/useIsMultiCtaPathwaysAvailable'
import { useMicrositeSectionProps } from '../../../../common/hooks/useMicrositeSectionProps'
import { useShowAppPromo } from '../../../../common/hooks/useShowAppPromo'
import { useUpdatedLyraSelectMicrositeConfig } from '../../../../common/hooks/useUpdatedLyraSelectMicrositeConfig'
import { useUpdatedMicrositeConfig } from '../../../../common/hooks/useUpdatedMicrositeConfig'
import { scrollToNextSibling } from '../../../../common/utils/micrositeUtils'
import { getLocalBaseUrl } from '../../../../common/utils/routingUtils'
import { logToSumoLogic, SUMO_CATEGORY } from '../../../../common/utils/userUtils'
import { setDeviceUUID, setToastContent } from '../../../../data/appGlobals/appGlobalsActions'
import { getDeviceUUID, getShouldShowLiveChat } from '../../../../data/appGlobals/appGlobalsSelectors'
import {
  getBeforeLaunch,
  getCustomerInfo,
  getCustomerLaunchDate,
  getCustomerName,
  getCustomerPropertiesMap,
  getHideDependentField,
  getIsCoachingEnabled,
  getIsCustomerCareTeam,
  getIsCustomerLyra,
  getIsTherapyEnabled,
  getLegalNotice,
  getLibraryDisabled,
  getNumberOfSessions,
  getNumberOfSessionsByProgram,
  getProgramMembershipType,
} from '../../../../data/customer/customerSelectors'
import { trackEventWithObj } from '../../../../data/mixpanel'
import { TrackEventWithObjAction } from '../../../../data/mixpanel/types'
import { useAppDispatch } from '../../../../data/storeConfiguration/store'
import {
  getIsLoggedOutUserAssumedInternationalAndOfInternationalCustomer,
  getIsUserLoggedIn,
  getUser,
} from '../../../../data/user/userSelectors'
import SecureIndexRedirector from '../../../indexRedirector/IndexRedirector'
import { setDirectLinkSource } from '../../../onboard/data/onboardDataActions'
import { setSelectedPathwayOption } from '../../../register/data/registerActions'
import { doPageLoad, postPrefilterCustomer } from '../../data/micrositeActions'

interface HomepageProps {
  isCareTeamPage: boolean
  isUserLoggedIn: boolean
  actions: {
    doPageLoad: (customerName: string, eventProps?: Dict, page?: string) => void
    trackEventWithObj: TrackEventWithObjAction
    setToastContent: (content: ToastContent) => void
    setDirectLinkSource: (source: DirectLinkSource) => void
  }
  customerName: string
  customerDisplayName: string
  membershipType: PROGRAM_MEMBERSHIP_TYPES
  user: User
  beforeLaunch: boolean
  startDate?: string
  isLyrian: boolean
  showLiveChat: boolean
  isUserAssumedInternational: boolean
  customerPropertiesMap?: CustomerPropertiesMap
  legalNotice?: React.ReactNode
  libraryDisabled?: boolean
  micrositeConfig?: MicrositeConfig
  numberOfSessions?: string
  isDependentDisabled?: boolean
  numberOfSessionsByProgram?: SessionLimitByProgram
  isCoachingEnabled: boolean
  isTherapyEnabled: boolean
  isAutoEnrollmentVariant?: boolean
}

const Homepage: React.FunctionComponent<HomepageProps> = ({
  isCareTeamPage,
  isUserLoggedIn,
  actions: { doPageLoad, trackEventWithObj, setDirectLinkSource },
  customerName,
  customerDisplayName,
  libraryDisabled,
  isAutoEnrollmentVariant = false,
}) => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const { isPathwaysMicrositeMultiCTAEnabled } = useFlags()

  const {
    coverageModalTitleText,
    coverageModalContents,
    costMessagingModalCoverageInfo,
    numSessions,
    numberOfSessionByProgram,
    displayVisitsPerIssuePerYear,
    displayVisitsPerIssuePerYearByProgram,
  } = useGetCostMessagingInfo()

  const pathwaysVersion = useGetPathwaysVersion()

  const { availableOptions, reasons, showMultiCTAPathways } = useIsMultiCtaPathwaysAvailable()
  const [isHeaderDark, setIsHeaderDark] = useState(true)

  useEffect(() => {
    const handleScroll = () => setIsHeaderDark(window.scrollY <= 100)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const { colors, breakpoints, isAuroraTheme } = useTheme()
  const { isLyraSelect } = useContext(ProductTypeContext)
  const { isMinWidthLaptop } = breakpoints
  const ldClient = useLDClient()
  const { formatMessage } = useIntl()
  const { activeLanguage } = useLyraIntl()

  const deviceUUID = useSelector(getDeviceUUID)

  const { search } = location
  const [searchParams] = useSearchParams()
  const IS_TESTING = window.Cypress && (process.env.NODE_ENV === 'development' || process.env.NODE_ENV.includes('test'))

  const pathwaysType =
    (isPathwaysMicrositeMultiCTAEnabled && showMultiCTAPathways) || isAutoEnrollmentVariant
      ? PATHWAYS_TYPE.MULTIPLE
      : PATHWAYS_TYPE.SINGLE
  const updatedMicrositeConfig = useUpdatedMicrositeConfig(pathwaysType)
  const lyraSelectMicrositeConfig = useUpdatedLyraSelectMicrositeConfig()
  const { micrositeSectionProps, startChat } = useMicrositeSectionProps({ trackEventWithObj, isAutoEnrollmentVariant })
  const isCostMessagingEnabled = useIsCostMessagingEnabled()
  const defaultMixpanelEntryPoint = useDefaultMixpanelEntryPoint()
  // Generate a unique reference id so that we can use it to track the user after they are navigated from care.lyrahealth.com to the customer's microsite
  const care_reference_id = IS_TESTING ? CYPRESS_CARE_REFERENCE_ID : uuidv4()

  // Reset pathway option if user navigates to homepage microsite
  useEffect(() => {
    dispatch(setSelectedPathwayOption(undefined))
  }, [dispatch])

  useEffect(() => {
    if (location.hash) {
      const timeout = setTimeout(() => {
        scroller.scrollTo(location.hash.replace('#', ''), { smooth: true })
      }, 500)
      return () => clearTimeout(timeout)
    }
    return
  }, [location])

  useEffect(() => {
    const page = isAutoEnrollmentVariant ? PATHWAYS_PAGE.AUTO_ENROLLMENT_PATHWAYS : PATHWAYS_PAGE.CUSTOMER_MICROSITE

    if (customerName && defaultMixpanelEntryPoint !== null) {
      doPageLoad(
        customerName,
        {
          ...pathwaysVersion,
          [COMMON_MIXPANEL_PROPERTIES.LD_DEVICE_UUID]: deviceUUID,
          ...(customerName !== 'care' && {
            [COMMON_MIXPANEL_PROPERTIES.CUSTOMER_NAME]: customerName,
          }),
          ...defaultMixpanelEntryPoint,
          ...(search.includes('entryPoint=FindEmployerPage') && {
            [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: `${COMMON_MIXPANEL_PAGE.FIND_EMPLOYER} page`,
          }),
          [COMMON_MIXPANEL_PROPERTIES.CARE_REFERENCE_ID]: searchParams.get('care_reference_id') ?? care_reference_id,
        },
        page,
      )
    }
  }, [
    customerName,
    doPageLoad,
    pathwaysVersion,
    deviceUUID,
    search,
    trackEventWithObj,
    isPathwaysMicrositeMultiCTAEnabled,
    ldClient,
    defaultMixpanelEntryPoint,
    isAutoEnrollmentVariant,
    searchParams,
    care_reference_id,
  ])

  useEffect(() => {
    if (customerName) {
      console.debug(
        `Customer: ${customerName} has multi cta pathways: ${
          showMultiCTAPathways ? "'visible'" : "'hidden'"
        } with # of available options: ${availableOptions.join(',')}, reasons: ${JSON.stringify(reasons)}`,
      )
      logToSumoLogic(IS_PRODUCTION ? SUMO_CATEGORY.PROD_DEBUG : SUMO_CATEGORY.STAGING_DEBUG, '', {
        message: 'pathways',
        customerName,
        showMultiCTAPathways,
        reasons,
        availableOptions,
      })
    }
  }, [customerName, showMultiCTAPathways, reasons, availableOptions])

  const { showAppPromoBanner, setShowAppPromoBanner, showAppPromoBottomSheet, setShowAppPromoBottomSheet } =
    useShowAppPromo({ promoBottomSheetEnabled: true })

  const handleFindEmployerSubmit = async (values: FindEmployerFormValues) => {
    const customerName = values.FindEmployerInputField
    const unauthenticatedRedirectUrl = window.sessionStorage.getItem(UNAUTHENTICATED_REDIRECT_URL)
    trackEventWithObj({
      [COMMON_MIXPANEL_PROPERTIES.EVENT]: REGISTRATION_EVENTS.SUBMIT_ORG_NAME,
      [COMMON_MIXPANEL_PROPERTIES.PAGE]: COMMON_MIXPANEL_PAGE.FIND_EMPLOYER,
      [COMMON_MIXPANEL_PROPERTIES.TEXT_INPUT]: values.FindEmployerInputField,
    })

    const customerDomain = await dispatch(postPrefilterCustomer({ customerName }))

    // Extract UTM parameters from current URL to pass to customer microsite
    const currentSearchParams = new URLSearchParams(location.search)
    const utmParams = new URLSearchParams()

    // Add entryPoint parameter
    utmParams.set('entryPoint', 'FindEmployerPage')

    // Add care reference id
    utmParams.set('care_reference_id', care_reference_id)

    // Add unauthenticatedRedirectUrl to query params so we can set it after origin change
    unauthenticatedRedirectUrl && utmParams.set('unauthenticatedRedirectUrl', unauthenticatedRedirectUrl)

    // Pass through UTM parameters if they exist
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign']
    utmKeys.forEach((key) => {
      const value = currentSearchParams.get(key)
      if (value) {
        utmParams.set(key, value)
      }
    })

    // Check for findEmployerSubmitRedirect query param
    const findEmployerSubmitRedirect = currentSearchParams.get('findEmployerSubmitRedirect')

    if (customerDomain) {
      const baseUrl = IS_TESTING ? getLocalBaseUrl(false, customerName) : customerDomain
      const pathname = findEmployerSubmitRedirect ? `/${findEmployerSubmitRedirect}` : ''
      const url = `${baseUrl}${pathname}?${utmParams.toString()}`
      window.location.href = url
    } else {
      trackEventWithObj({
        [COMMON_MIXPANEL_PROPERTIES.EVENT]: REGISTRATION_EVENTS.INPUT_INCORRECT_COMPANY_NAME,
        [COMMON_MIXPANEL_PROPERTIES.PAGE]: COMMON_MIXPANEL_PAGE.FIND_EMPLOYER,
        [COMMON_MIXPANEL_PROPERTIES.TEXT_INPUT]: values.FindEmployerInputField,
      })
      // Also pass UTM parameters to the referral organization link
      const referralParams = new URLSearchParams()
      referralParams.set('organization', customerName)
      utmKeys.forEach((key) => {
        const value = currentSearchParams.get(key)
        if (value) {
          referralParams.set(key, value)
        }
      })
      const link = IS_TESTING ? getLocalBaseUrl(true) : PRODUCTION_REFER_ORGANIZATION_LINK
      window.location.href = link + `?${referralParams.toString()}`
    }
  }

  if (isCareTeamPage) {
    return (
      <StickyView
        headerComponent={isAuroraTheme ? <Header isBlurBackground={!isHeaderDark} /> : <Header />}
        headerBackgroundColor={colors.backgroundTransparent}
        sticky={!isMinWidthLaptop}
        overlayHeader
      >
        <ContentLayout>
          <FindEmployer onSubmit={handleFindEmployerSubmit} />
          <LegalFooter />
        </ContentLayout>
      </StickyView>
    )
  }

  if (isUserLoggedIn && (!isAutoEnrollmentVariant || !isPathwaysMicrositeMultiCTAEnabled)) {
    // Redirect to secure/index if user is logged in
    return <SecureIndexRedirector />
  }

  const defaultWrapperProps = {
    headerComponent: (
      <ThemeProvider theme={isAuroraTheme ? Theme.DARK : Theme.LIGHT}>
        <>
          {showAppPromoBanner && <AppPromoBanner />}
          <Header
            logoFillColor={isAuroraTheme ? colors.textInverse : undefined}
            isBlurBackground={isAuroraTheme ? !isHeaderDark : undefined}
          />
        </>
      </ThemeProvider>
    ),
    headerBackgroundColor:
      !isMinWidthLaptop || !isAuroraTheme ? colors.backgroundPrimary : colors.backgroundTransparent,
    sticky: !isMinWidthLaptop,
    overlayHeader: isMinWidthLaptop,
  }

  const isDefaultHomepage = !isLyraSelect && !isAutoEnrollmentVariant

  const autoEnrollmentPathwaysConfig = {
    hero: {
      pathwaysType: 'multiple' as PATHWAYS_TYPE,
      isAutoEnrollmentPathways: isAutoEnrollmentVariant,
    },
    mobileAppPromoBanner: {
      hideSection: true,
    },
    customerBrandedBanner: {
      hideSection: true,
    },
    offeringsCarousel: {
      hideSection: false,
      hideMeetWithExpert: false,
      hideWellnessResources: false,
      hideAllOfferings: false,
    },
    mobileAppPromo: {
      pathwaysType: 'multiple' as PATHWAYS_TYPE,
      hideSection: true,
    },
    lyraMembership: {
      hideSection: false,
      hideNumberOfSessions: false,
    },
    selfCare: {
      hideSection: true,
    },
    testimonials: {
      hideSection: true,
    },
    careNavigator: {
      hideSection: true,
    },
    faq: {
      hideSection: false,
    },
  }

  const trackCoverageModalPress = (isOpen: boolean) => {
    trackEventWithObj({
      event: isOpen ? COVERAGE_MESSAGING_EVENTS.OPEN_COVERAGE_MODAL : COVERAGE_MESSAGING_EVENTS.CLOSE_COVERAGE_MODAL,
      page: PATHWAYS_PAGE.AUTO_ENROLLMENT_PATHWAYS,
    })
  }

  const understandYourCoverageButton = (
    <CoverageMessagingButton
      coverageModalTitleText={coverageModalTitleText}
      coverageModalContents={coverageModalContents}
      costMessagingModalCoverageInfo={costMessagingModalCoverageInfo}
      isCostMessagingEnabled={isCostMessagingEnabled}
      customerName={customerDisplayName}
      numSessions={numSessions}
      numberOfSessionByProgram={numberOfSessionByProgram}
      displayVisitsPerIssuePerYear={displayVisitsPerIssuePerYear}
      displayVisitsPerIssuePerYearByProgram={displayVisitsPerIssuePerYearByProgram}
      trackCoverageModalPress={trackCoverageModalPress}
    />
  )

  const autoEnrollmentHeader = (
    <DefaultHeader
      lyraLogo={
        <Pressable
          onPress={() => {
            trackEventWithObj({
              event: AUTO_ENROLLMENT_PATHWAYS_EVENTS.CLICK_HEADER_LYRA_LOGO,
              page: PATHWAYS_PAGE.AUTO_ENROLLMENT_PATHWAYS,
            })
            navigate(PAGE_ROUTES.HOMEBASE)
          }}
          accessibilityRole='button'
          accessibilityLabel={formatMessage({
            defaultMessage: 'Lyra Logo',
            description: 'Button that takes user to home page',
          })}
          testID={tID('Header-Lyra-Logo')}
        >
          <LyraLogo width={54} height={31} fillColor={colors.icons.lyraLogoPrimaryIcon.fill} />
        </Pressable>
      }
      handleLogoClick={() => {
        trackEventWithObj({
          event: AUTO_ENROLLMENT_PATHWAYS_EVENTS.CLICK_HEADER_LYRA_LOGO,
          page: PATHWAYS_PAGE.AUTO_ENROLLMENT_PATHWAYS,
        })
        navigate(PAGE_ROUTES.HOMEBASE)
      }}
      shouldHideLoginButton={true}
      loggedOutButtons={[
        <TextButton
          key='skip-button'
          onPress={() => {
            trackEventWithObj({
              event: AUTO_ENROLLMENT_PATHWAYS_EVENTS.CLICK_HEADER_SKIP,
              page: PATHWAYS_PAGE.AUTO_ENROLLMENT_PATHWAYS,
            })
            navigate(PAGE_ROUTES.HOMEBASE)
          }}
          text={formatMessage({
            defaultMessage: 'Skip',
            description: 'Skip button text to skip to next page',
          })}
        />,
      ]}
      ghost={true}
      shouldShowCareNavigatorHeaderEntrypoint={false}
    />
  )

  const wrapperProps = isAutoEnrollmentVariant ? {} : defaultWrapperProps

  return (
    <StickyView {...wrapperProps}>
      <ContentLayout>
        <>
          {isDefaultHomepage && (
            <PathwaysMicrositeHomePage
              micrositeConfig={updatedMicrositeConfig}
              heroSectionProps={micrositeSectionProps.heroSectionProps}
              faqSectionProps={micrositeSectionProps.faqSectionProps}
              careNavigatorSectionProps={micrositeSectionProps.careNavigatorSectionProps}
              lyraOfferingsSectionProps={micrositeSectionProps.lyraOfferingsSectionProps}
              selfCareSectionProps={micrositeSectionProps.selfCareSectionProps}
              legalAndCopyrightFooterProps={micrositeSectionProps.legalAndCopyrightFooterProps}
              membershipSectionProps={micrositeSectionProps.membershipSectionProps}
              mobileAppPromoSectionProps={micrositeSectionProps.mobileAppPromoSectionProps}
              mobileAppPromoBannerProps={micrositeSectionProps.mobileAppPromoBannerProps}
              customerBrandedBannerProps={{ customerName }}
            />
          )}
          {isLyraSelect && (
            <LyraSelectMicrositeHomePage
              selectHomepageHeroSectionProps={{
                onExploreOnYourOwnCTAPress: () => {
                  scrollToNextSibling('LyraSelectHeroSection-heroContainer')
                },
                onGetStartedPress: () => {
                  navigate('/register/connect')
                },
              }}
              micrositeConfig={lyraSelectMicrositeConfig}
              faqSectionProps={micrositeSectionProps.faqSectionProps}
              legalAndCopyrightFooterProps={micrositeSectionProps.legalAndCopyrightFooterProps}
              careNavigatorSectionProps={micrositeSectionProps.careNavigatorSectionProps}
              membershipSectionProps={micrositeSectionProps.membershipSectionProps}
              selfCareSectionProps={micrositeSectionProps.selfCareSectionProps}
              mobileAppPromoSectionProps={micrositeSectionProps.mobileAppPromoSectionProps}
              mobileAppPromoBannerProps={micrositeSectionProps.mobileAppPromoBannerProps}
            />
          )}
          {isAutoEnrollmentVariant && (
            <PathwaysMicrositeHomePage
              micrositeConfig={autoEnrollmentPathwaysConfig}
              heroSectionProps={{
                ...micrositeSectionProps.heroSectionProps,
                isAutoEnrollmentPathways: isAutoEnrollmentVariant,
                coverageButton: understandYourCoverageButton,
                libraryDisabled: libraryDisabled,
                onProviderLedCardPress: () => {
                  setDirectLinkSource(DirectLinkSource.AUTO_ENROLLMENT_PATHWAYS)
                  trackEventWithObj({
                    event: AUTO_ENROLLMENT_PATHWAYS_EVENTS.CLICK_THERAPY_COACHING_PATHWAY,
                    page: PATHWAYS_PAGE.AUTO_ENROLLMENT_PATHWAYS,
                  })
                  navigate(PAGE_ROUTES.PATHWAYS.FORKED_TRIAGE, {
                    state: { mixpanelEntryPoint: PATHWAYS_PAGE.AUTO_ENROLLMENT_PATHWAYS },
                  })
                },
                onSelfCareCardPress: () => {
                  trackEventWithObj({
                    event: AUTO_ENROLLMENT_PATHWAYS_EVENTS.CLICK_MEDITATIONS_COURSES_PATHWAY,
                    page: PATHWAYS_PAGE.AUTO_ENROLLMENT_PATHWAYS,
                  })
                  navigate(PAGE_ROUTES.PATHWAYS.SELF_CARE, {
                    state: { mixpanelEntryPoint: PATHWAYS_PAGE.AUTO_ENROLLMENT_PATHWAYS },
                  })
                },
                onExploreOnYourOwnCTAPress: () => {
                  trackEventWithObj({
                    event: AUTO_ENROLLMENT_PATHWAYS_EVENTS.CLICK_EXPLORE_ON_YOUR_OWN_PATHWAY,
                    page: PATHWAYS_PAGE.AUTO_ENROLLMENT_PATHWAYS,
                  })
                  navigate(PAGE_ROUTES.HOMEBASE, {
                    state: { mixpanelEntryPoint: PATHWAYS_PAGE.AUTO_ENROLLMENT_PATHWAYS },
                  })
                },
                customNavHeader: autoEnrollmentHeader,
                onWellnessCheckinCardPress: undefined,
              }}
              faqSectionProps={{
                ...micrositeSectionProps.faqSectionProps,
                onQuestionPress: (title: string) => {
                  trackEventWithObj({
                    event: PATHWAYS_EVENTS.CLICK_FAQS,
                    page: PATHWAYS_PAGE.AUTO_ENROLLMENT_PATHWAYS,
                    [PATHWAYS_PROPERTIES.FAQ_QUESTION]: title,
                  })
                },
                isAutoEnrollmentPathways: isAutoEnrollmentVariant,
              }}
              careNavigatorSectionProps={{}}
              lyraOfferingsSectionProps={{
                ...micrositeSectionProps.lyraOfferingsSectionProps,
                meetWithAnExpertTabPress: () => {
                  trackEventWithObj({
                    event: PATHWAYS_EVENTS.VIEW_MEET_WITH_AN_EXPERT_TAB,
                    page: PATHWAYS_PAGE.AUTO_ENROLLMENT_PATHWAYS,
                  })
                },
                wellnessResourcesTabPress: () => {
                  trackEventWithObj({
                    event: PATHWAYS_EVENTS.VIEW_CONTENT_LIBRARY_TAB,
                    page: PATHWAYS_PAGE.AUTO_ENROLLMENT_PATHWAYS,
                  })
                },
                lyraOfferingsTabPress: () => {
                  trackEventWithObj({
                    event: PATHWAYS_EVENTS.VIEW_ALL_OFFERINGS_TAB,
                    page: PATHWAYS_PAGE.AUTO_ENROLLMENT_PATHWAYS,
                  })
                },
                lyraOfferingsCtaPress: undefined,
              }}
              selfCareSectionProps={{
                language: activeLanguage,
              }}
              legalAndCopyrightFooterProps={{
                ...micrositeSectionProps.legalAndCopyrightFooterProps,
                showCopyright: false,
              }}
              membershipSectionProps={micrositeSectionProps.membershipSectionProps}
              mobileAppPromoSectionProps={micrositeSectionProps.mobileAppPromoSectionProps}
              mobileAppPromoBannerProps={micrositeSectionProps.mobileAppPromoBannerProps}
              customerBrandedBannerProps={{ customerName }}
            />
          )}
          {showAppPromoBottomSheet && (
            <AppPromoBottomSheet
              onContinueInBrowserPress={() => {
                window.localStorage.setItem(MOBILE_PROMO_DISMISSED, 'true')
                setShowAppPromoBottomSheet(false)
                setShowAppPromoBanner(true)
              }}
            />
          )}
        </>
        <ChatLive
          hideButton
          startChat={startChat}
          mixpanelEntryPoint={PATHWAYS_ENTRY_POINTS.CARE_NAVIGATOR}
          mixpanelPage={COMMON_MIXPANEL_PAGE.MICROSITE}
        />
      </ContentLayout>
    </StickyView>
  )
}

function mapStateToProps(state: $TSFixMe) {
  return {
    isCareTeamPage: getIsCustomerCareTeam(state),
    customerName: getCustomerName(state),
    customerDisplayName: getCustomerInfo(state),
    isUserLoggedIn: getIsUserLoggedIn(state),
    membershipType: getProgramMembershipType(state),
    beforeLaunch: getBeforeLaunch(state),
    startDate: getCustomerLaunchDate(state),
    user: getUser(state),
    isLyrian: getIsCustomerLyra(state),
    showLiveChat: getShouldShowLiveChat(state),
    isUserAssumedInternational: getIsLoggedOutUserAssumedInternationalAndOfInternationalCustomer(state),
    customerPropertiesMap: getCustomerPropertiesMap(state),
    legalNotice: getLegalNotice(state),
    deviceUUID: getDeviceUUID(state),
    libraryDisabled: getLibraryDisabled(state),
    numberOfSessions: getNumberOfSessions(state),
    numberOfSessionsByProgram: getNumberOfSessionsByProgram(state),
    isDependentDisabled: getHideDependentField(state),
    isCoachingEnabled: getIsCoachingEnabled(state),
    isTherapyEnabled: getIsTherapyEnabled(state),
  }
}

function mapDispatchToProps(dispatch: $TSFixMe) {
  return {
    actions: bindActionCreators(
      { doPageLoad, trackEventWithObj, setToastContent, setDirectLinkSource, setDeviceUUID },
      dispatch,
    ),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(toJS(Homepage))
